import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useJobAllQuery } from '../../store/api/userApiSlice';
import JobFilterPanel from './JobFilterPanel';
import JobCard from './JobCard';
import './JobSearchResults.css';
import { IoSearch, IoChevronDown } from 'react-icons/io5';
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import CustomDropdown from '../CollegeFinder/CustomDropdown';

const JobSearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';

  // Fetch data
  const { data, isLoading, error } = useJobAllQuery();
  // Adjust data access based on actual API response structure (often data.data or similar)
  const jobs = data?.data || data?.jobs || (Array.isArray(data) ? data : []) || [];
  
  // Local state
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [sortBy, setSortBy] = useState('Date Posted');
  const [displayCount, setDisplayCount] = useState(10);
  const [activeFilters, setActiveFilters] = useState({});

  // Sync with URL
  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
  }, [searchParams]);

  // Filter Logic
  const filteredJobs = jobs.filter(job => {
    // 1. Text Search
    let matchesSearch = true;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const title = job.title || job.jobTitle || '';
      const company = job.companyDetails?.companyName || job.companyName || '';
      const loc = job.location || job.companyDetails?.city || '';
      const skills = job.skillsRequired || job.tags || [];

      matchesSearch = (
        title.toLowerCase().includes(q) ||
        company.toLowerCase().includes(q) ||
        loc.toLowerCase().includes(q) ||
        skills.some(t => (typeof t === 'string' ? t : t.name).toLowerCase().includes(q))
      );
    }

    // 2. Panel Filters
    let matchesFilters = true;
    
    // Experience Filter
    if (matchesFilters && activeFilters.experience) {
       const expFilter = activeFilters.experience; // e.g. "Fresher", "1-3 Years", "8+ Years"
       const jobStart = job.experienceStart || 0;
       const jobEnd = job.experienceEnd || jobStart; // fallback

       if (expFilter === "Fresher") {
           // Fresher usually means 0 experience
           if (jobStart > 0) matchesFilters = false;
       } else if (expFilter.includes("+")) {
           // "8+ Years" -> 8
           const minExp = parseInt(expFilter.split('+')[0]);
           // If job requires less than 8 years max, it might not overlap? 
           // Usually "8+" meant for candidates with >8 years.
           // If job is 7-10 years, does it match 8+? Yes. 
           // Logic: does the job experience range overlap with [8, infinity]?
           // Overlap: jobEnd >= 8
           if (jobEnd < minExp) matchesFilters = false;
       } else {
           // "1-3 Years" -> 1, 3
           const parts = expFilter.split(' ')[0].split('-');
           if (parts.length === 2) {
               const minExp = parseInt(parts[0]);
               const maxExp = parseInt(parts[1]);
               // Check overlap between [jobStart, jobEnd] and [minExp, maxExp]
               // Overlap exists if: jobStart <= maxExp && jobEnd >= minExp
               if (!(jobStart <= maxExp && jobEnd >= minExp)) {
                   matchesFilters = false;
               }
           }
       }
    }

    // Salary Filter
    if (matchesFilters && activeFilters.salary) {
        const salFilter = activeFilters.salary; // "< 3 LPA", "3-6 LPA", "15+ LPA"
        // Normalize job salary to LPA
        let jobLPA = 0;
        if (job.salary) {
            // Assume salary in API is annual absolute number (e.g. 1500000)
            jobLPA = job.salary / 100000; 
        }

        if (salFilter.includes("<")) {
            // "< 3 LPA"
            const max = parseInt(salFilter.replace('<', '').trim());
            if (jobLPA >= max) matchesFilters = false;
        } else if (salFilter.includes("+")) {
            // "15+ LPA"
            const min = parseInt(salFilter.split('+')[0]);
            if (jobLPA < min) matchesFilters = false;
        } else {
             // "3-6 LPA"
             const parts = salFilter.split(' ')[0].split('-');
             if (parts.length === 2) {
                 const min = parseInt(parts[0]);
                 const max = parseInt(parts[1]);
                 if (jobLPA < min || jobLPA > max) matchesFilters = false;
             }
        }
    }

    // Job Type
    if (matchesFilters && activeFilters.jobType) {
        // API: jobType: { name: "Exprienced" } or string
        const selectedType = activeFilters.jobType.toLowerCase();
        
        let typeName = "";
        if (job.jobType && typeof job.jobType === 'object') {
            typeName = job.jobType.name || "";
        } else if (typeof job.jobType === 'string') {
            typeName = job.jobType;
        }

        // Handle possible mismatch: "Full Time" vs "Experienced"
        // Just checking inclusion
        if (!typeName.toLowerCase().includes(selectedType)) {
            // Special fallback? If selected "Full Time" but API has "Experienced", maybe it's not a match.
            matchesFilters = false;
        }
    }

    // Role
    if (matchesFilters && activeFilters.role) {
         const selectedRole = activeFilters.role.toLowerCase();
         // Check title, description, or explicit role field
         const title = (job.title || "").toLowerCase();
         const role = (job.role || "").toLowerCase(); // Recruiter object has role? 
         const companyRole = (job.companyDetails?.role || "").toLowerCase();

         if (!title.includes(selectedRole) && !role.includes(selectedRole) && !companyRole.includes(selectedRole)) {
            matchesFilters = false;
         }
    }

    return matchesSearch && matchesFilters;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
        navigate(`/jobs?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setDisplayCount(10);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  const handleJobClick = (jobId) => {
      navigate(`/jobs/${jobId}`);
  };

  return (
    <div className="search-results-container">
      {/* Sidebar */}
      <div className="search-sidebar">
        <JobFilterPanel onApply={handleApplyFilters} />
      </div>

      {/* Content */}
      <div className="search-content">
        
        {/* Header */}
        <div className="search-results-header-container">
          <h2 className="search-results-title">
            Search results {filteredJobs.length > 0 && `(${filteredJobs.length})`}
          </h2>
          <div className="search-controls-wrapper">
             <form onSubmit={handleSearch} className="search-bar-wrapper">
               <IoSearch className="search-icon" />
               <input 
                 type="text" 
                 placeholder="Search jobs by title, company..." 
                 className="college-search-input"
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
               />
             </form>
             <div className="sort-wrapper">
               <div style={{ width: '180px' }}>
                 <CustomDropdown 
                   options={['Date Posted', 'Salary: High to Low', 'Experience']}
                   value={sortBy}
                   onChange={setSortBy}
                   placeholder="SORT BY"
                 />
               </div>
             </div>
          </div>
        </div>

        {/* Loading/Error/Empty States */}
        {isLoading && (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading Jobs...</div>
        )}
        
        {!isLoading && filteredJobs.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center' }}>No jobs found.</div>
        )}

        {/* Results List */}
        {!isLoading && filteredJobs.length > 0 && (
            <>
                <div className="results-list">
                    {filteredJobs.slice(0, displayCount).map(job => (
                        <JobCard 
                            key={job._id || job.id} 
                            job={job} 
                            onClick={() => handleJobClick(job._id || job.id)} 
                        />
                    ))}
                </div>

                <div className="search-footer-actions">
                  {displayCount < filteredJobs.length && (
                    <button className="load-more-btn" onClick={handleLoadMore}>
                      LOAD MORE <IoChevronDown />
                    </button>
                  )}
                  <button className="back-to-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    BACK TO TOP <MdKeyboardDoubleArrowUp />
                  </button>
                </div>
            </>
        )}

      </div>
    </div>
  );
};

export default JobSearchResults;
